import { useEffect, useState, useCallback } from "react";
import { getOrders, exportOrders } from "@/lib/procureApi";
import { STAGE, fmtInt, ageTone } from "@/lib/format";
import { MagnifyingGlass, CaretLeft, CaretRight, ArrowSquareOut, DownloadSimple, Spinner, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

const StageBadge = ({ t }) => {
  const s = STAGE[t] || { color: "#64748B" };
  return (
    <span className="inline-flex items-center gap-1.5 rounded-sm px-2 py-0.5 font-mono text-[11px] font-semibold" style={{ background: `${s.color}22`, color: s.color }}>
      {t}
    </span>
  );
};

export const OrdersTable = ({ filters, snapshotId, onOpenRef2, refreshKey }) => {
  const [criticalOnly, setCriticalOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 50 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => setPage(1), [debounced, filters, snapshotId, criticalOnly]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getOrders({ ...filters, snapshot_id: snapshotId, search: debounced, page, page_size: 50, critical_only: criticalOnly || undefined });
      setData(res);
    } finally {
      setLoading(false);
    }
  }, [filters, debounced, page, snapshotId, criticalOnly, refreshKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));
  const [exporting, setExporting] = useState(false);

  const doExport = async () => {
    setExporting(true);
    try {
      await exportOrders({ ...filters, snapshot_id: snapshotId, search: debounced, critical_only: criticalOnly || undefined });
      toast.success(`Exported ${fmtInt(data.total)} order line(s) to Excel`);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="col-span-12 border border-[#E3E7EC] bg-[#FFFFFF]">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E3E7EC] p-4">
        <div>
          <h3 className="text-sm font-semibold text-[#0F172A]">Order Lines</h3>
          <p className="pf-label mt-0.5">{fmtInt(data.total)} lines · click a row for the Reference 2 journey</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="orders-critical-toggle"
            onClick={() => setCriticalOnly((v) => !v)}
            title="Show only Production Driver lines"
            className={`flex h-9 items-center gap-1.5 rounded-sm border px-3 text-sm font-bold transition-colors ${
              criticalOnly ? "border-[#B91C1C] bg-[#FDE2E4] text-[#B91C1C]" : "border-[#FDE2E4] bg-white text-[#B91C1C] hover:bg-[#FDE2E4]"
            }`}
          >
            <Lightning size={14} weight="fill" />
            <span data-testid="orders-critical-count">{fmtInt(data.critical_total || 0)}</span> Production Drivers
          </button>
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input
              data-testid="ref2-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search Ref 2, order #, description"
              className="h-9 w-72 rounded-sm border border-[#E3E7EC] bg-[#F1F3F6] pl-8 pr-3 text-sm text-[#0F172A] outline-none placeholder:text-[#9CA3AF] focus:border-[#F0323E]"
            />
          </div>
          <button
            data-testid="orders-export"
            onClick={doExport}
            disabled={exporting || data.total === 0}
            title="Export these order lines to Excel"
            className="flex h-9 items-center gap-2 rounded-sm border border-[#CAD1DB] bg-white px-3 text-sm font-semibold text-[#0F172A] transition-colors hover:bg-[#EDEFF3] disabled:opacity-50"
          >
            {exporting ? <Spinner size={15} className="animate-spin" /> : <DownloadSimple size={15} weight="bold" />}
            Excel
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm" data-testid="orders-table">
          <thead>
            <tr className="border-b border-[#E3E7EC] text-left">
              {["Type", "Order #", "Item #", "Ref 2", "Description", "Buyer", "Branch", "Next Status", "Age"].map((h) => (
                <th key={h} className="whitespace-nowrap px-4 py-2.5 pf-label">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.items.map((r, i) => (
              <tr
                key={`${r.order_number}-${r.line_number}-${i}`}
                data-testid={`order-row-${i}`}
                onClick={() => r.reference2 && onOpenRef2(r.reference2)}
                data-critical={r.critical ? "true" : "false"}
                className={`group cursor-pointer border-b border-[#E3E7EC]/50 transition-colors ${
                  r.critical ? "bg-[#FDE2E4] font-bold [&_td]:!text-[#B91C1C] hover:bg-[#FBCFD3]" : "hover:bg-[#EDEFF3]"
                }`}
              >
                <td className="px-4 py-2"><StageBadge t={r.order_type} /></td>
                <td className="whitespace-nowrap px-4 py-2 font-mono text-[#64748B]">{r.order_number}</td>
                <td className="whitespace-nowrap px-4 py-2 font-mono text-[#64748B]">
                  <span className="inline-flex items-center gap-1">{r.critical && <Lightning size={12} weight="fill" />}{r.item_number || "—"}</span>
                </td>
                <td className="whitespace-nowrap px-4 py-2 font-mono text-[#F0323E]">
                  <span className="inline-flex items-center gap-1 group-hover:underline">
                    {r.reference2 || "—"}
                    {r.reference2 && <ArrowSquareOut size={12} className="opacity-0 group-hover:opacity-100" />}
                  </span>
                </td>
                <td className="max-w-[280px] truncate px-4 py-2 text-[#0F172A]">{r.description || "—"}</td>
                <td className="whitespace-nowrap px-4 py-2 text-[#64748B]">{r.buyer}</td>
                <td className="whitespace-nowrap px-4 py-2 text-[#64748B]">{r.branch_plant || "—"}</td>
                <td className="max-w-[200px] truncate px-4 py-2 text-[#64748B]">{r.next_status_code || "—"}</td>
                <td className={`whitespace-nowrap px-4 py-2 text-right font-mono font-semibold ${ageTone(r.age_days)}`}>
                  {r.age_days ?? "—"}{r.age_days != null ? "d" : ""}
                </td>
              </tr>
            ))}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-[#9CA3AF]">No lines match your filters</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between border-t border-[#E3E7EC] px-4 py-3">
        <span className="font-mono text-xs text-[#9CA3AF]">Page {data.page} / {totalPages}</span>
        <div className="flex gap-1">
          <button
            data-testid="page-prev"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="flex h-8 w-8 items-center justify-center rounded-sm border border-[#E3E7EC] text-[#64748B] transition-colors hover:bg-[#EDEFF3] disabled:opacity-30"
          >
            <CaretLeft size={15} />
          </button>
          <button
            data-testid="page-next"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="flex h-8 w-8 items-center justify-center rounded-sm border border-[#E3E7EC] text-[#64748B] transition-colors hover:bg-[#EDEFF3] disabled:opacity-30"
          >
            <CaretRight size={15} />
          </button>
        </div>
      </div>
    </div>
  );
};
